import React, { useState } from 'react';
import { useStore } from '../../../store/useStore';
import { FileText, Download, Link as LinkIcon, UploadCloud, Folder, Search, File as FileIcon, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { UploadModal } from '../../ui/UploadModal';

export const MateriaisApoio: React.FC = () => {
  const { supportMaterials, setSupportMaterials } = useStore();
  const [activeFolder, setActiveFolder] = useState('Iscas Digitais (Landing Pages)');
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const folders = ['Iscas Digitais (Landing Pages)', 'Catálogos & Portfólios', 'Tabelas de Preços'];

  const mockFiles = [
    { id: '1', name: 'Ebook_Como_Vender_Mais_2024.pdf', size: '2.4 MB', date: '10 Jun 2024', folder: 'Iscas Digitais (Landing Pages)', type: 'pdf' },
    { id: '2', name: 'Checklist_Auditoria_Instagram.pdf', size: '850 KB', date: '05 Jun 2024', folder: 'Iscas Digitais (Landing Pages)', type: 'pdf' },
    { id: '3', name: 'Catalogo_Verao_24.pdf', size: '15.2 MB', date: '01 Jun 2024', folder: 'Catálogos & Portfólios', type: 'pdf' },
  ];

  const allFiles = supportMaterials.iscasDigitais?.length > 0 ? supportMaterials.iscasDigitais : mockFiles;
  const filteredFiles = allFiles.filter((f: any) => f.folder === activeFolder);

  const copyLink = () => {
    toast.success('Link copiado para a área de transferência!');
  };

  const handleUpload = (files: File[], title: string) => {
    if (files.length > 0) {
      const newFiles = files.map((file, idx) => ({
        id: Math.random().toString(36).substr(2, 9),
        name: files.length > 1 ? `${title}_${idx + 1}` : file.name,
        size: (file.size / 1024 / 1024).toFixed(1) + ' MB',
        date: 'Hoje',
        folder: activeFolder,
        type: file.type.includes('pdf') ? 'pdf' : 'doc'
      }));
      setSupportMaterials({ iscasDigitais: [...allFiles, ...newFiles] });
      toast.success(`${newFiles.length} arquivo(s) enviado(s) com sucesso!`);
    }
  };

  return (
    <div className="space-y-6 h-full flex flex-col">
      <UploadModal 
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title="Upload de Material de Apoio"
        accept=".pdf,.doc,.docx"
        multiple
        onUpload={handleUpload}
      />
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Materiais de Apoio</h3>
          <p className="text-sm text-gray-500 mt-1">Gerencie iscas digitais, catálogos e tabelas de preços usados para conversão.</p>
        </div>
        <button onClick={() => setIsUploadOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
          <UploadCloud size={16} />
          Fazer Upload de PDF
        </button>
      </div>

      <div className="flex gap-6 h-[500px]">
        {/* Sidebar Folders */}
        <div className="w-64 shrink-0 flex flex-col gap-1 border-r border-gray-100 pr-6">
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-3">Pastas de Arquivos</h4>
          {folders.map(folder => (
            <button
              key={folder}
              onClick={() => setActiveFolder(folder)}
              className={`flex items-center justify-between px-3 py-3 rounded-lg text-sm transition-colors text-left ${activeFolder === folder ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <div className="flex items-center gap-3">
                <Folder size={18} className={activeFolder === folder ? 'text-indigo-500' : 'text-gray-400'} />
                {folder}
              </div>
            </button>
          ))}
        </div>

        {/* File Manager Content */}
        <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar flex flex-col">
          {/* Top Bar for Files */}
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-bold text-gray-900">{activeFolder}</h4>
            <div className="relative w-64">
               <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
               <input type="text" placeholder="Buscar arquivo..." className="w-full pl-9 pr-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm flex-1 flex flex-col">
            <div className="grid grid-cols-12 gap-4 p-4 border-b border-gray-100 bg-gray-50/50 text-xs font-bold text-gray-500 uppercase tracking-wider">
               <div className="col-span-6">Nome do Arquivo</div>
               <div className="col-span-2 text-center">Tamanho</div>
               <div className="col-span-2 text-center">Data</div>
               <div className="col-span-2 text-right">Ações</div>
            </div>
            
            <div className="divide-y divide-gray-100 flex-1 overflow-y-auto">
              {filteredFiles.map((file: any) => (
                <div key={file.id} className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-gray-50 transition-colors group">
                   <div className="col-span-6 flex items-center gap-3">
                     <div className="w-10 h-10 rounded-lg bg-red-50 text-red-500 flex items-center justify-center shrink-0">
                       <FileIcon size={20} />
                     </div>
                     <span className="text-sm font-medium text-gray-900 truncate pr-4">{file.name}</span>
                   </div>
                   <div className="col-span-2 text-center text-sm text-gray-500">{file.size}</div>
                   <div className="col-span-2 text-center text-sm text-gray-500">{file.date}</div>
                   <div className="col-span-2 flex items-center justify-end gap-2">
                     <button onClick={copyLink} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Copiar Link Direto">
                        <LinkIcon size={16} />
                     </button>
                     <button className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Fazer Download">
                        <Download size={16} />
                     </button>
                   </div>
                </div>
              ))}
              
              {filteredFiles.length === 0 && (
                <div className="p-12 flex flex-col items-center justify-center text-center">
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 border border-gray-100">
                    <FileText size={24} className="text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-900">Nenhum arquivo nesta pasta</p>
                  <p className="text-xs text-gray-500 mt-1 mb-4">Arraste seus PDFs para cá</p>
                  <button onClick={() => fileInputRef.current?.click()} className="text-xs font-medium bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors">
                    Fazer Upload
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
